/**
 * How the source payload should be interpreted for chart compatibility.
 */
export type DataMode =
  | "BarChart"
  | "LineChart"
  | "HorizontalBarChart"
  | "AverageReturns"
  | "Statistics"
  | "Holdings";

/** Supported chart renderers. */
export type ChartType =
  | "bar_chart"
  | "line_chart"
  | "horizontal_bar_chart"
  | "split_horizontal_bar"
  | "average_returns"
  | "statistics"
  | "holdings";

/** ETL / upload pipeline status for a catalog row (updated by Lambda when raw/ matches this source). */
export type IngestionStatus = "idle" | "processing" | "ready" | "error";
export type PdfStatus = "pending" | "processing" | "ready" | "error";

export interface SourceMetadata {
  title?: string;
  description?: string;
  attribution?: string;
  source?: string;
  owner?: string;
  /** RFC 3339 / ISO 8601 timestamp when the source record was created. */
  created?: string;
  /** RFC 3339 / ISO 8601 timestamp of last catalog or payload refresh. */
  lastModified?: string;
  ingestionStatus?: IngestionStatus;
  /** RFC 3339 / ISO 8601 when `ingestionStatus` last changed. */
  ingestionUpdatedAt?: string;
  /** Short error message when `ingestionStatus` is `error`. */
  ingestionError?: string;
  /** Relative or absolute URL for the generated chart PDF artifact. */
  pdfUrl?: string;
  pdfStatus?: PdfStatus;
  /** RFC 3339 / ISO 8601 when `pdfStatus` last changed. */
  pdfUpdatedAt?: string;
  /** Short error message when `pdfStatus` is `error`. */
  pdfError?: string;
  [key: string]: unknown;
}

/** One row in the sources catalog (served as `sources.json`). */
export interface SourceDefinition {
  id: string;
  url: string;
  mode: DataMode;
  /** Deprecated source ids that resolve to this row (mirrored in SDK `SOURCE_ID_ALIASES`). */
  aliases?: string[];
  metadata?: SourceMetadata;
  options?: Record<string, unknown>;
}

export interface SourcesCatalog {
  sources: SourceDefinition[];
}

/** One chart renderer entry from `charts.json` (documentation / integrators). */
export interface ChartDefinition {
  id: ChartType;
  name: string;
  description?: string;
  allowedModes: DataMode[];
  /**
   * @deprecated Use {@link ChartDefinition.renderer}. Retained because it is part of
   * the published OpenAPI contract; drop it on an intentional version bump.
   */
  highchartsModule?: "stock" | "core" | "dom";
  /** `d3` = drawn as SVG by d3; `dom` = HTML-only layout. */
  renderer?: "d3" | "dom";
}

export interface ChartsCatalog {
  version?: string;
  charts: ChartDefinition[];
}

/** One route row from `api/index.json`. */
export interface ApiRoute {
  id: string;
  method: "GET";
  path: string;
  summary: string;
  description?: string;
  href: string;
}

export interface ApiIndex {
  version: string;
  openapi?: { yaml?: string; json?: string };
  schemas?: Record<string, string>;
  routes: ApiRoute[];
}

/** HTTP Basic credentials for protected chart-library origins. */
export interface BasicAuthCredentials {
  username: string;
  password: string;
}

/** Optional settings for SDK HTTP helpers (`fetchJson`, catalog loaders, etc.). */
export interface ClientFetchOptions {
  basicAuth?: BasicAuthCredentials;
  /**
   * Cache-busting token appended as `?v=`, to get past a browser copy cached before the
   * origin sent `Cache-Control`. Omit for a token fixed at page load (the default), pass
   * `false` to disable, or pass a string to pin your own release version.
   */
  cacheBust?: boolean | string;
}

export interface IncompatibleDetail {
  chartType: ChartType;
  sourceId: string;
  sourceMode: DataMode;
  allowedModes: DataMode[];
  /** Set when validation fails inside a tabbed pane. */
  paneIndex?: number;
  paneTab?: string;
}

/** Layout passed to chart renderers when mounted inside a tabbed shell. */
export type ChartLayout = "standalone" | "embedded";

/** One tab pane inside `type: "tabbed"`. */
export interface TabbedChartPane {
  /** Tab button label and default pane heading. */
  tab: string;
  type: ChartType;
  source: string;
  /**
   * Tabbed shell disclaimer key (not used for `bar_chart` / `line_chart`; copy comes from dataset `disclaimer`).
   * Use `"characteristics"` for portfolio characteristics copy on split/horizontal panes.
   */
  disclaimer?: string | null | false;
  options?: Pick<NtenChartUserOptions, "title" | "subtitle">;
}

/** Extra knobs passed to `ntenChart` (title, subtitle, hooks). */
export interface NtenChartUserOptions {
  title?: string | null;
  /** Overrides API / metadata subtitle (e.g. “As of …” line under the title). */
  subtitle?: string | null;
  /**
   * Disclaimer text. For single-chart mounts: overrides or supplements dataset copy
   * (`bar_chart`, `line_chart`, `average_returns`, `statistics`, `holdings`).
   * For `type: "tabbed"`: shell-level disclaimer below the whole tabbed block (not per tab).
   * Supports `<b>`, `<strong>`, `<br />`, and newlines.
   */
  disclaimer?: string | null;
  /**
   * @deprecated Use `disclaimer` instead. Still accepted for statistics for backwards compatibility.
   */
  legalText?: string | null;
  /** Called when source mode does not support the requested chart type. */
  onIncompatible?: (detail: IncompatibleDetail) => void;
  [key: string]: unknown;
}

/** Shared options for single-chart and tabbed mounts. */
export interface NtenChartBaseOptions {
  target: string | HTMLElement;
  sourcesCatalogUrl?: string;
  chartsCatalogUrl?: string;
  catalog?: SourcesCatalog;
  basicAuth?: BasicAuthCredentials;
  /** See {@link ClientFetchOptions.cacheBust}. Applies to catalog and dataset reads. */
  cacheBust?: boolean | string;
  options?: NtenChartUserOptions;
}

/** Single chart mount (`bar_chart`, `line_chart`, etc.). */
export interface NtenSingleChartOptions extends NtenChartBaseOptions {
  type: ChartType;
  source: string;
  dataOverride?: unknown;
}

/** Tabbed Performance-style mount; each pane has its own type, source, and optional disclaimer. */
export interface NtenTabbedChartOptions extends NtenChartBaseOptions {
  type: "tabbed";
  charts: TabbedChartPane[];
  /** Per-pane payload overrides keyed by source id. */
  dataOverrides?: Record<string, unknown>;
}

export type NtenChartOptions = NtenSingleChartOptions | NtenTabbedChartOptions;

export function isTabbedChartOptions(options: NtenChartOptions): options is NtenTabbedChartOptions {
  return options.type === "tabbed";
}

export interface ChartHandle {
  destroy(): void;
}

/**
 * @deprecated Use {@link NtenChartUserOptions} instead.
 */
export type An26ChartUserOptions = NtenChartUserOptions;

/**
 * @deprecated Use {@link NtenChartOptions} instead.
 */
export type An26ChartOptions = NtenChartOptions;
